import { isEmpty } from 'lodash'
import {
  startLoading,
  stopLoading,
} from '../../../common/modules/loading/actions'
import { LoadingTypes } from '../../../common/modules/loading/reducer'
import { AxiosError, AxiosResponse } from 'axios'
import { AppDispatch, RootState } from '../../../store'
import {
  fetchPartnerIqTableData,
  fetchPartnerIqOverviewData,
  putPartnerIqIcpFormData,
  fetchPartnerIqIcpFormData,
  fetchPartnerIqProfileData,
  fetchPartnerIqOpportunitiesReport,
  fetchPartnerIqTableReport,
  fetchPartnerIqSegments,
} from '../../api/PartnerIq'
import {
  OverviewComponentData,
  PartnerIqTableData,
  PartnerIqOverviewResponse,
  ICPData,
  ProfileData,
  SegmentsData,
} from './reducer'
import { camelize } from 'casing'
import { updateAppAlert } from '../../../common/modules/appAlert/actions'
import { getErrorMessages } from '../../../common/utils/error'
import {
  ICPCalculationInProgressMessage,
  ICPCreateSuccessMessage,
  ICPUpdateSuccessMessage,
  RequestFailureMessage,
} from '../../../common/utils/messagesContants'
import { errorLogger } from '../../../common/utils/errorLogger'
import FileDownload from 'js-file-download'

export enum PartnerIQActionTypes {
  SET_PARTNER_IQ_TABLE_DATA = 'SET_PARTNER_IQ_TABLE_DATA',
  SET_PARTNER_IQ_OVERVIEW_DATA = 'SET_PARTNER_IQ_OVERVIEW_DATA',
  CLEAR_PARTNER_IQ_TABLE_DATA = 'CLEAR_PARTNER_IQ_TABLE_DATA',
  SET_PARTNER_IQ_PROFILE_DATA = 'SET_PARTNER_IQ_PROFILE_DATA',
  CLEAR_PARTNER_IQ_PROFILE_DATA = 'CLEAR_PARTNER_IQ_PROFILE_DATA',
  SET_PARTNER_IQ_ICP_FORM_DATA = 'SET_PARTNER_IQ_ICP_FORM_DATA',
  SET_PARTNER_IQ_SEGMENTS_DATA = 'SET_PARTNER_IQ_SEGMENTS_DATA',
  CLEAR_PARTNER_IQ_SEGMENTS_DATA = 'CLEAR_PARTNER_IQ_SEGMENTS_DATA',
}

export const setPartnerIqOverviewData = (data: OverviewComponentData[]) => ({
  type: PartnerIQActionTypes.SET_PARTNER_IQ_OVERVIEW_DATA,
  payload: data,
})

export const setPartnerIqTableData = (data: PartnerIqTableData) => ({
  type: PartnerIQActionTypes.SET_PARTNER_IQ_TABLE_DATA,
  payload: data,
})

export const clearPartnerIqTableData = () => ({
  type: PartnerIQActionTypes.CLEAR_PARTNER_IQ_TABLE_DATA,
})

export const setPartnerIqProfileData = (data: ProfileData) => ({
  type: PartnerIQActionTypes.SET_PARTNER_IQ_PROFILE_DATA,
  payload: data,
})

export const clearPartnerIqProfileData = () => ({
  type: PartnerIQActionTypes.CLEAR_PARTNER_IQ_PROFILE_DATA,
})

export const setPartnerIqIcpFormData = (data: ICPData | null) => ({
  type: PartnerIQActionTypes.SET_PARTNER_IQ_ICP_FORM_DATA,
  payload: data,
})

export const setPartnerIqSegmentsData = (data: SegmentsData) => ({
  type: PartnerIQActionTypes.SET_PARTNER_IQ_SEGMENTS_DATA,
  payload: data,
})

export const clearPartnerIqSegmentsData = () => ({
  type: PartnerIQActionTypes.CLEAR_PARTNER_IQ_SEGMENTS_DATA,
})
export const getPartnerIqTableData =
  (
    partnerId: string,
    sortBy?: string,
    sortOrder?: 'asc' | 'desc',
    segment?: string,
    icpFitMin?: number,
    icpFitMax?: number,
    search?: string,
    searchType?: string,
    append?: boolean
  ) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    await dispatch(startLoading(LoadingTypes.FETCH_PARTNER_IQ_TABLE_DATA))
    try {
      let title: string | undefined
      let contactName: string | undefined
      let location: string | undefined

      if (search) {
        const normalizedSearchType = searchType?.toLowerCase() || 'contact name'
        if (normalizedSearchType === 'title') {
          title = search
        } else if (normalizedSearchType === 'location') {
          location = search
        } else {
          contactName = search
        }
      }

      const response = await fetchPartnerIqTableData(
        partnerId,
        sortBy,
        sortOrder,
        segment,
        icpFitMin,
        icpFitMax,
        title,
        contactName,
        location
      )
      if (!isEmpty(response?.data)) {
        const camelizedData = camelize(response?.data) as PartnerIqTableData
        if (append) {
          const existingTableData = getState().partnerIQ.tableData
          const existingContacts = existingTableData.contacts || []
          camelizedData.contacts = [
            ...existingContacts,
            ...(camelizedData.contacts || []),
          ]
          camelizedData.totalCount = existingTableData.totalCount
        }
        await dispatch(setPartnerIqTableData(camelizedData))
      }
    } catch (error: any) {
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse<ErrorResponse>

      dispatch(
        updateAppAlert({
          message: getErrorMessages([RequestFailureMessage])(errorResponse),
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      const globalState = getState()
      errorLogger({ globalState })(
        error as Error,
        errorResponse?.data?.track_id
      )
    } finally {
      await dispatch(stopLoading(LoadingTypes.FETCH_PARTNER_IQ_TABLE_DATA))
    }
  }

const transformPartnerIqOverviewData = (
  data: PartnerIqOverviewResponse
): OverviewComponentData[] => {
  const overviewData: OverviewComponentData[] = []

  // Transform top_performers_segments into "Top performing segments" overview
  if (data.topPerformersSegments) {
    const segmentsArray = data.topPerformersSegments
    const validSegments = segmentsArray
      .filter(item => item !== null && item !== undefined)
      .map(item => {
        return typeof item === 'string' ? item : item.value
      })

    if (validSegments.length > 0) {
      overviewData.push({
        title: 'Top performing segments',
        type: validSegments.map(segment => ({
          title: segment,
        })),
      })
    }
  }

  // Transform top_performers_locations into "Top performing territory" overview
  if (data.topPerformersLocations) {
    const locationsArray = data.topPerformersLocations
    const validLocations = locationsArray
      .filter(item => item !== null && item !== undefined)
      .map(item => {
        return typeof item === 'string' ? item : item.value
      })

    if (validLocations.length > 0) {
      overviewData.push({
        title: 'Top performing territory',
        type: validLocations.map(location => ({
          title: location,
        })),
      })
    }
  }

  // Transform top_performers into "Top performer" overview
  if (data.topPerformers?.length) {
    overviewData.push({
      title: 'Top performer',
      type: data.topPerformers.map(performer => ({
        title: performer.contactName,
        contactId: performer.contactId,
        segment: performer.segment,
        location: performer.location,
      })),
    })
  }

  // Transform opportunities into "Opportunities" overview
  if (data.opportunities) {
    const { inProgress, closedWon, closedLost } = data.opportunities
    const opportunityItems: Array<{ title: string; value?: number }> = []

    if (inProgress) {
      opportunityItems.push({
        title: `${inProgress.count} In progress`,
        value: inProgress.value,
      })
    }

    if (closedWon) {
      opportunityItems.push({
        title: `${closedWon.count} Closed won`,
        value: closedWon.value,
      })
    }

    if (closedLost) {
      opportunityItems.push({
        title: `${closedLost.count} Closed lost`,
        value: closedLost.value,
      })
    }

    if (opportunityItems.length > 0) {
      overviewData.push({
        title: 'Opportunities',
        type: opportunityItems,
      })
    }
  }

  return overviewData
}

export const getPartnerIqSegments =
  (partnerId: string) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    await dispatch(startLoading(LoadingTypes.FETCH_PARTNER_IQ_SEGMENTS_DATA))
    try {
      const response = await fetchPartnerIqSegments(partnerId)
      if (!isEmpty(response?.data)) {
        const camelizedData = camelize(response?.data) as SegmentsData
        await dispatch(setPartnerIqSegmentsData(camelizedData))
      }
    } catch (error: any) {
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse<ErrorResponse>
      dispatch(
        updateAppAlert({
          message: getErrorMessages([RequestFailureMessage])(errorResponse),
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      const globalState = getState()
      errorLogger({ globalState })(
        error as Error,
        errorResponse?.data?.track_id
      )
    } finally {
      await dispatch(stopLoading(LoadingTypes.FETCH_PARTNER_IQ_SEGMENTS_DATA))
    }
  }

export const getPartnerIqOverviewData =
  (partnerId: string) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    await dispatch(startLoading(LoadingTypes.FETCH_PARTNER_IQ_TABLE_DATA))
    try {
      const response = await fetchPartnerIqOverviewData(partnerId)
      if (!isEmpty(response?.data)) {
        const camelizedData = camelize(
          response?.data
        ) as PartnerIqOverviewResponse
        const overviewData = transformPartnerIqOverviewData(camelizedData)
        await dispatch(setPartnerIqOverviewData(overviewData))
      }
    } catch (error: any) {
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse<ErrorResponse>

      dispatch(
        updateAppAlert({
          message: getErrorMessages([RequestFailureMessage])(errorResponse),
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      const globalState = getState()
      errorLogger({ globalState })(
        error as Error,
        errorResponse?.data?.track_id
      )
    } finally {
      await dispatch(stopLoading(LoadingTypes.FETCH_PARTNER_IQ_TABLE_DATA))
    }
  }

export const getPartnerIqIcpFormData =
  (partnerId: string) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    await dispatch(startLoading(LoadingTypes.FETCH_PARTNER_IQ_ICP_FORM_DATA))
    try {
      const response = await fetchPartnerIqIcpFormData(partnerId)
      if (!isEmpty(response?.data)) {
        const camelizedData = camelize(response?.data) as unknown as {
          id?: string
          partnerId?: string
          name?: string
          annualRevenue?: string | null | string[]
          industry?: string[]
          employeeSize?: string[]
          segment?: string[]
          cloudTechnologyStack?: string[]
          geographicalRegion?: string[]
          marketplacePresence?: string[]
          userId?: string
        }

        // Transform the response to match ICPData format
        const transformedData: ICPData = {
          id: camelizedData.id,
          partnerId: camelizedData.partnerId,
          icpName: camelizedData.name || '',
          // Handle annualRevenue: API returns string | null, but we need string[]
          annualRevenue:
            camelizedData.annualRevenue === null ||
            camelizedData.annualRevenue === undefined
              ? []
              : Array.isArray(camelizedData.annualRevenue)
                ? camelizedData.annualRevenue
                : [camelizedData.annualRevenue],
          industry: camelizedData.industry || [],
          employeeSize: camelizedData.employeeSize || [],
          segment: camelizedData.segment || [],
          cloudTechnologyStack: camelizedData.cloudTechnologyStack || [],
          geographicalRegion: camelizedData.geographicalRegion || [],
          marketplacePresence: camelizedData.marketplacePresence || [],
          userId: camelizedData.userId || '',
        }

        await dispatch(setPartnerIqIcpFormData(transformedData))
      }
    } catch (error: any) {
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse<ErrorResponse>

      dispatch(
        updateAppAlert({
          message: getErrorMessages([RequestFailureMessage])(errorResponse),
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      const globalState = getState()
      errorLogger({ globalState })(
        error as Error,
        errorResponse?.data?.track_id
      )
    } finally {
      await dispatch(stopLoading(LoadingTypes.FETCH_PARTNER_IQ_ICP_FORM_DATA))
    }
  }

export const getPartnerIqProfileData =
  (partnerId: string, contactId: string) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    await dispatch(startLoading(LoadingTypes.FETCH_PARTNER_IQ_PROFILE_DATA))
    try {
      const response = await fetchPartnerIqProfileData(partnerId, contactId)
      if (!isEmpty(response?.data)) {
        const camelizedData = camelize(response?.data) as ProfileData
        await dispatch(setPartnerIqProfileData(camelizedData))
      }
    } catch (error: any) {
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse<ErrorResponse>
      dispatch(
        updateAppAlert({
          message: getErrorMessages([RequestFailureMessage])(errorResponse),
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      const globalState = getState()
      errorLogger({ globalState })(
        error as Error,
        errorResponse?.data?.track_id
      )
    } finally {
      await dispatch(stopLoading(LoadingTypes.FETCH_PARTNER_IQ_PROFILE_DATA))
    }
  }

export const updatePartnerIqIcpFormData =
  (partnerId: string, data: ICPData) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    await dispatch(startLoading(LoadingTypes.UPDATE_PARTNER_IQ_ICP_FORM_DATA))
    try {
      const state = getState()
      const userId = data.userId || state.userProfile?.userProfile?.id

      const apiData = {
        name: data.icpName,
        annualRevenue: data.annualRevenue || [],
        industry: data.industry || [],
        employeeSize: data.employeeSize || [],
        segment: data.segment || [],
        cloudTechnologyStack: data.cloudTechnologyStack || [],
        geographicalRegion: data.geographicalRegion || [],
        marketplacePresence: data.marketplacePresence || [],
        userId: userId,
        ...(data.id && { id: data.id }),
      }

      const response = await putPartnerIqIcpFormData(partnerId, apiData)
      if (!isEmpty(response?.data)) {
        const camelizedData = camelize(response?.data) as ICPData
        const transformedData: ICPData = {
          ...camelizedData,
          icpName: (camelizedData as unknown as { name?: string })?.name || '',
        }
        await dispatch(setPartnerIqIcpFormData(transformedData))
        await dispatch(
          updateAppAlert({
            message: ICPCalculationInProgressMessage,
            messageType: 'SUCCESS',
            autoClose: true,
          })
        )
      }
    } catch (error: any) {
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse<ErrorResponse>

      dispatch(
        updateAppAlert({
          message: getErrorMessages([RequestFailureMessage])(errorResponse),
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      const globalState = getState()
      errorLogger({ globalState })(
        error as Error,
        errorResponse?.data?.track_id
      )
    } finally {
      await dispatch(stopLoading(LoadingTypes.UPDATE_PARTNER_IQ_ICP_FORM_DATA))
    }
  }

export const getPartnerIqTableReportFile =
  (
    partnerId: string,
    year: string,
    page?: number,
    pageSize?: number,
    sortBy?: string,
    sortOrder?: 'asc' | 'desc',
    segment?: string,
    icpFitMin?: number,
    icpFitMax?: number,
    contactName?: string,
    title?: string,
    location?: string
  ) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    await dispatch(startLoading(LoadingTypes.FETCH_PARTNER_IQ_TABLE_REPORT))

    try {
      const response = await fetchPartnerIqTableReport(
        partnerId,
        page,
        pageSize,
        sortBy,
        sortOrder,
        segment,
        icpFitMin,
        icpFitMax,
        contactName,
        title,
        location
      )

      const filename = `contacts_report_${year}.csv`
      FileDownload(response.data, filename)
    } catch (error: any) {
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse<ErrorResponse>

      dispatch(
        updateAppAlert({
          message: getErrorMessages([RequestFailureMessage])(errorResponse),
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      const globalState = getState()
      errorLogger({ globalState })(
        error as Error,
        errorResponse?.data?.track_id
      )
      throw error
    } finally {
      await dispatch(stopLoading(LoadingTypes.FETCH_PARTNER_IQ_TABLE_REPORT))
    }
  }

export const getPartnerIqOpportunitiesReportFile =
  (partnerId: string, contactId: string, year: string) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    await dispatch(
      startLoading(LoadingTypes.FETCH_PARTNER_IQ_OPPORTUNITIES_REPORT)
    )

    try {
      const response = await fetchPartnerIqOpportunitiesReport(
        partnerId,
        contactId
      )

      const filename = `opportunities_report_${year}.csv`
      FileDownload(response.data, filename)
    } catch (error: any) {
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse<ErrorResponse>

      dispatch(
        updateAppAlert({
          message: getErrorMessages([RequestFailureMessage])(errorResponse),
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      const globalState = getState()
      errorLogger({ globalState })(
        error as Error,
        errorResponse?.data?.track_id
      )
      throw error
    } finally {
      await dispatch(
        stopLoading(LoadingTypes.FETCH_PARTNER_IQ_OPPORTUNITIES_REPORT)
      )
    }
  }
